import React, { useState } from 'react';
import { SparklesIcon, ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { validationApi } from '../services/validationApi';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatIsoDate } from '../utils/formatDateValue';

interface AppliedEntry {
  field: string;
  value?: unknown;
  priorValue?: unknown;
  source?: string | null;
  snippet?: string;
  message?: string;
  undone?: boolean;
  superseded?: boolean;
}

interface Props {
  // One field, or several related fields that share a single mark (e.g. violationTypes +
  // otherViolationTypes) so we don't render duplicate badges for the same finding.
  field: string | string[];
  caseId: string;
  ai?: { autoApplied?: AppliedEntry[] };
  onChange?: () => void;
}

const fmt = (v: unknown): React.ReactNode => {
  if (v === null || v === undefined || v === '') return <span className="italic text-gray-400">empty</span>;
  const asDate = formatIsoDate(v);
  if (asDate) return asDate;
  if (Array.isArray(v)) return v.map((x) => (x && typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const humanizeField = (f: string): string => {
  const leaf = (f.split('.').pop() || f).replace(/\[.*?\]/g, '');
  return leaf.replace(/([A-Z])/g, ' $1').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/^./, (c) => c.toUpperCase());
};

// Inline marker shown next to a field (or group of related fields) the AI auto-filled. Renders
// nothing unless there is an active (non-undone) auto-applied entry. One badge even when several
// fields were filled by the same finding; the popup lists each with its own Undo.
const AiFieldMark: React.FC<Props> = ({ field, caseId, ai, onChange }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [open, setOpen] = useState(false);
  const [busyField, setBusyField] = useState<string | null>(null);

  const fields = Array.isArray(field) ? field : [field];
  const entries = (ai?.autoApplied || []).filter((e) => fields.includes(e.field) && !e.undone && !e.superseded);
  if (entries.length === 0) return null;

  const canAct = user?.role === 'ADMIN' || user?.role === 'APPROVER';
  // If every entry carries the same message (same finding), show it once at the top.
  const sharedMessage = entries.every((e) => e.message === entries[0].message) ? entries[0].message : null;

  const undo = async (f: string) => {
    setBusyField(f);
    try {
      await validationApi.undoAutoApplied(caseId, f);
      showToast('success', 'Change undone');
      onChange?.();
    } catch (e: any) {
      showToast('error', e.response?.data?.error || 'Undo failed');
    } finally {
      setBusyField(null);
    }
  };

  return (
    <span className="relative inline-block align-middle">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Auto-filled by AI — click for source"
        className="ml-1 inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 hover:bg-violet-200"
      >
        <SparklesIcon className="h-3 w-3" /> AI Auto-filled
      </button>
      {open && (
        <div className="absolute z-20 mt-1 left-0 w-72 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-3 text-xs">
          <div className="font-medium text-gray-800 dark:text-gray-100 mb-1">Auto-filled by AI from relevant sources</div>
          {sharedMessage && <div className="text-gray-600 dark:text-gray-300 mb-2">{sharedMessage}</div>}
          <div className="space-y-2">
            {entries.map((entry) => (
              <div key={entry.field} className="border-t border-gray-100 dark:border-gray-700 pt-2 first:border-t-0 first:pt-0">
                {entries.length > 1 && (
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">{humanizeField(entry.field)}</div>
                )}
                {!sharedMessage && entry.message && <div className="text-gray-600 dark:text-gray-300 mb-1">{entry.message}</div>}
                {entry.snippet && (
                  <div className="text-gray-500 italic mb-1">
                    “{entry.snippet}”
                    {entry.source && (<> — <a href={entry.source} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800">source</a></>)}
                  </div>
                )}
                {!entry.snippet && entry.source && (
                  <div className="mb-1"><a href={entry.source} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800">source</a></div>
                )}
                <div className="text-gray-400 mb-1.5">was: {fmt(entry.priorValue)}</div>
                {canAct && (
                  <button
                    type="button"
                    disabled={busyField === entry.field}
                    onClick={() => undo(entry.field)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 font-semibold text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    <ArrowUturnLeftIcon className="h-3.5 w-3.5" /> Undo{entries.length > 1 ? ' this' : ''}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </span>
  );
};

export default AiFieldMark;
